import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { FaMicrophone } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";

import Navbar from "../../component/Navbar/Navbar";
import Footer from "../../component/Footer/Footer";
import { useSmartDownload } from "../../hooks/useSmartDownload";

import heroImg from "./Assets/image.png";
import ArticleSidebar from "./ArticleSidebar";
import WhatCountsSection from "./WhatCountsSection";
import WhatMakesDifferentSection from "./WhatMakesDifferentSection";
import WindowsComparisonSection from "./WindowsComparisonSection";
import MacComparisonSection from "./MacComparisonSection";
import GoogleDocsComparisonSection from "./GoogleDocsComparisonSection";
import EditingGapSection from "./EditingGapSection";
import TryItSection from "./TryItSection";
import UnlimitedDictationSection from "./UnlimitedDictationSection";
import FAQSection from "./FAQSection";
import { ARTICLE_SECTIONS, SCROLL_OFFSET, GRADIENT_TEXT } from "./sections";

const STATS = [
  { value: "3×", label: "Faster than typing" },
  { value: "99%", label: "Uptime guaranteed" },
  { value: "10k+", label: "Active users" },
];

const VoiceTypingVsBuiltInDictation = () => {
  const navigate = useNavigate();
  const smartDownload = useSmartDownload();
  const [activeSection, setActiveSection] = useState(ARTICLE_SECTIONS[0].id);

  const handleScrollTo = (id) => {
    const element = document.getElementById(id);
    if (!element) return;

    setActiveSection(id);
    const top =
      element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
  };

  // Scroll spy: highlight the last section whose top has passed the probe line.
  useEffect(() => {
    const handleScroll = () => {
      const probe = window.scrollY + SCROLL_OFFSET + 40;
      let current = null;

      ARTICLE_SECTIONS.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top + window.scrollY <= probe) {
          current = section.id;
        }
      });

      if (current) setActiveSection(current);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {"Voice Typing vs Built-In Dictation: Windows, Mac & Google Docs Compared"}
        </title>
        <meta
          name="description"
          content="Windows Voice Access, Mac Dictation and Google Docs voice typing compared against AI voice typing. See which one actually saves you time and where each falls short."
        />
        <link
          rel="canonical"
          href="https://zerotyping.ai/blog/voice-typing-vs-built-in-dictation"
        />
      </Helmet>

      <div className="relative overflow-hidden w-full bg-[#C9E9FC] border-b-[1.25px] border-[#E6ECF5] font-inter">
        {/* Floating colour glow - same orbiting-circle treatment as the Home hero */}
        <div
          className="pointer-events-none absolute inset-0 z-0 hidden md:block"
          aria-hidden="true"
        >
          <svg
            className="absolute inset-0 h-full w-full"
            viewBox="0 0 1440 774"
            preserveAspectRatio="none"
          >
            <path
              id="dictationOrbitPath"
              d="M0,0 H1440 V774 H0 Z"
              fill="none"
              stroke="transparent"
            />

            <circle
              r="300"
              fill="url(#dictationGlow)"
              filter="url(#dictationBlur)"
            >
              <animateMotion repeatCount="indefinite" dur="20s" rotate="auto">
                <mpath href="#dictationOrbitPath" />
              </animateMotion>
            </circle>

            <circle
              r="300"
              fill="url(#dictationGlow)"
              filter="url(#dictationBlur)"
            >
              <animateMotion
                repeatCount="indefinite"
                dur="20s"
                rotate="auto"
                keyPoints="1;0"
                keyTimes="0;1"
              >
                <mpath href="#dictationOrbitPath" />
              </animateMotion>
            </circle>

            <defs>
              <radialGradient id="dictationGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#3AB1F6" stopOpacity=".9" />
                <stop offset="100%" stopColor="#3AB1F6" stopOpacity="0" />
              </radialGradient>
              <filter
                id="dictationBlur"
                x="-100%"
                y="-100%"
                width="300%"
                height="300%"
              >
                <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
              </filter>
            </defs>
          </svg>
        </div>

        {/* Navigation Bar */}
        <Navbar />

        {/* Hero */}
        <div
          id="overview"
          className="max-w-[1440px] mx-auto pt-28 md:pt-36 pb-16 px-6 md:px-12 lg:px-[101px] relative z-10"
        >
          <div className="flex flex-col lg:flex-row items-start justify-between gap-10 lg:gap-[48px]">
            {/* Left: copy + actions */}
            <div className="w-full lg:w-[620px] xl:w-[700px] shrink-0 flex flex-col items-start text-left">
              {/*
                The breaks hold the designed three-line split from lg up
                ("Voice Typing vs Built-In / Dictation: Windows, Mac, /
                and Google Docs Compared"); below lg the column is too narrow
                for it, so the heading wraps freely.
              */}
              <h1 className="text-[#0A0D16] text-[36px] sm:text-[44px] lg:text-[44px] xl:text-[50px] font-bold leading-[1.2] lg:leading-[58px] xl:leading-[66px] tracking-[-1.35px] font-jakarta">
                Voice Typing vs Built-In{" "}
                <br className="hidden lg:block" />
                Dictation: Windows, Mac,{" "}
                <br className="hidden lg:block" />
                and Google Docs Compared
              </h1>

              <p className="mt-[25.76px] w-full max-w-[540px] font-inter text-[16px] font-normal leading-[29.6px] tracking-[0px] text-[#0A0D16B2]">
                Every major platform now ships with some form of dictation
                built in. Windows has Voice Access. Mac has Dictation. Google
                Docs has its own Voice Typing tool baked into the toolbar. On
                paper, that should be enough. You press a shortcut, you talk,
                words appear.
              </p>

              {/* Action buttons */}
              <div className="w-full flex flex-wrap lg:flex-nowrap items-center justify-center lg:justify-start gap-3 pt-[30.05px]">
                <button
                  type="button"
                  onClick={() => navigate("/demo")}
                  className="shrink-0 whitespace-nowrap bg-white hover:bg-slate-50 text-[#3AB1F6] font-semibold text-[14px] sm:text-[15px] px-5 py-3 rounded-full flex items-center gap-2.5 shadow-[0_4px_16px_rgba(0,0,0,0.06)] hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-full bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] flex items-center justify-center text-white shrink-0">
                    <FaMicrophone className="w-[14px] h-[14px]" />
                  </span>
                  <span>Try Zero Typing Free</span>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    smartDownload("voice_typing_vs_built_in_dictation_hero")
                  }
                  className="shrink-0 whitespace-nowrap bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#33A2E5] hover:to-[#386FE8] text-white font-semibold text-[14px] sm:text-[15px] px-5 py-3 rounded-full flex items-center gap-2.5 shadow-[0_4px_18px_rgba(58,177,246,0.35)] hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 cursor-pointer"
                >
                  <span className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-[#3AB1F6] shrink-0">
                    <FiDownload className="w-[14px] h-[14px]" />
                  </span>
                  <span>Download Zero Typing</span>
                </button>
              </div>
            </div>

            {/* Right: product visual + stats */}
            <div className="w-full flex-1 min-w-0 flex flex-col">
              <img
                src={heroImg}
                width={1173}
                height={858}
                alt="Side-by-side comparison of the same project update dictated with built-in dictation and with Zero Typing, which adds punctuation and formatting"
                className="animate-float block w-full h-auto"
              />

              <div className="mt-[25.76px]">
                <div className="grid grid-cols-3 gap-[12px]">
                  {STATS.map(({ value, label }) => (
                    <div key={label}>
                      <p className="font-jakarta text-[30px] sm:text-[38px] lg:text-[44px] font-bold leading-[1.15] tracking-[-0.8px]">
                        <span className={GRADIENT_TEXT}>{value}</span>
                      </p>
                      <p className="mt-[6px] font-inter text-[14px] sm:text-[15px] lg:text-[16px] font-normal leading-[24px] tracking-[0px] text-[#0A0D1699]">
                        {label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Article body: sticky section nav + content column */}
      <div className="w-full bg-white font-inter">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-[101px] pt-12 md:pt-16 lg:pt-24 pb-[64px] md:pb-[88px] lg:pb-[112px]">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-[63px]">
            <ArticleSidebar
              activeSection={activeSection}
              onSelect={handleScrollTo}
            />

            <div className="w-full lg:max-w-[815.58px] min-w-0 flex flex-col gap-[64px] md:gap-[80px] lg:gap-[96px]">
              <WhatCountsSection />
              <WhatMakesDifferentSection />
              <WindowsComparisonSection />
              <MacComparisonSection />
              <GoogleDocsComparisonSection />
              <EditingGapSection />

              {/* The two CTA panels read as one block, so they sit closer together */}
              <div className="flex flex-col gap-[25.76px]">
                <TryItSection />
                <UnlimitedDictationSection />
              </div>

              <FAQSection />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default VoiceTypingVsBuiltInDictation;
